import { WeaknessProfile } from 'constants/stats'
import { Strokes } from 'store/slices/pg-slice/interface'

/**
 * Analyzes strokes from the current typing session to identify weaknesses
 * for all users based on their just-completed test.
 *
 * This provides real-time, session-based weakness analysis that shows
 * users exactly what they struggled with in their most recent test.
 */

interface GetSessionWeaknessesProps {
  strokes: Strokes
  text: string
}

interface CharacterPerformance {
  char: string
  attempts: number
  mistakes: number
  mistakeRate: number
}

interface WordPerformance {
  word: string
  mistakes: number
}

/**
 * Extract weak characters from stroke data
 * Characters with multiple attempts or mistakes are considered weak
 */
const getWeakCharactersFromSession = (strokes: Strokes): string[] => {
  const characterMap = new Map<string, CharacterPerformance>()

  Object.values(strokes || {}).forEach((stroke) => {
    const char = stroke.characterToBeTyped

    // Skip whitespace for character analysis
    if (!char || char === ' ') return

    const existing = characterMap.get(char) || {
      char,
      attempts: 0,
      mistakes: 0,
      mistakeRate: 0,
    }

    // Count total attempts and mistakes
    stroke.attempts.forEach((attempt) => {
      existing.attempts++
      if (attempt.typedChar !== char) {
        existing.mistakes++
      }
    })

    existing.mistakeRate = existing.attempts > 0 ? existing.mistakes / existing.attempts : 0
    characterMap.set(char, existing)
  })

  // Filter characters with mistakes and sort by mistake rate
  const weakCharacters = Array.from(characterMap.values())
    .filter((perf) => perf.mistakes > 0 || perf.attempts > 1)
    .sort((a, b) => {
      // Prioritize by mistake rate, then by number of mistakes
      if (b.mistakeRate !== a.mistakeRate) {
        return b.mistakeRate - a.mistakeRate
      }
      return b.mistakes - a.mistakes
    })
    .slice(0, 5)
    .map((perf) => perf.char)

  return weakCharacters
}

/**
 * Extract weak words from stroke data
 * Words where user made mistakes are considered weak
 */
const getWeakWordsFromSession = (strokes: Strokes, text: string): string[] => {
  const words = text.split(/\s+/).filter((w) => w.length > 0)
  const strokeArray = Object.values(strokes || {})

  const wordPerformanceMap = new Map<string, WordPerformance>()

  let currentCharIndex = 0
  words.forEach((word) => {
    let wordMistakes = 0

    // Check each character in the word
    for (let i = 0; i < word.length; i++) {
      const stroke = strokeArray[currentCharIndex + i]
      if (!stroke) continue

      // Count mistakes (attempts > 1 or wrong character)
      if (stroke.attempts.length > 1 || !stroke.correctlyTyped) {
        wordMistakes++
      }
    }

    // Store word performance
    if (wordMistakes > 0) {
      const existing = wordPerformanceMap.get(word.toLowerCase()) || { word: word.toLowerCase(), mistakes: 0 }
      existing.mistakes += wordMistakes
      wordPerformanceMap.set(word.toLowerCase(), existing)
    }

    currentCharIndex += word.length + 1 // +1 for space
  })

  // Sort by mistakes and return top weak words
  const weakWords = Array.from(wordPerformanceMap.values())
    .sort((a, b) => b.mistakes - a.mistakes)
    .slice(0, 5)
    .map((perf) => perf.word)

  return weakWords
}

/**
 * Main function: Get session-based weakness profile
 * Returns a WeaknessProfile-compatible object from current session data
 *
 * @param strokes - Stroke data from the completed typing test
 * @param text - The text that was typed
 * @returns WeaknessProfile or null if insufficient data
 */
export const getSessionWeaknesses = ({ strokes, text }: GetSessionWeaknessesProps): WeaknessProfile | null => {
  // Need at least some strokes to analyze
  const strokeCount = Object.keys(strokes || {}).length
  if (strokeCount < 10) {
    return null
  }

  const weakCharacters = getWeakCharactersFromSession(strokes)
  const weakWords = getWeakWordsFromSession(strokes, text)

  // For session-based analysis, we don't have n-gram data
  // but we can extract bigrams from weak characters if needed
  const ngrams: string[] = []

  // If we have at least 2 weak characters, create simple bigrams
  if (weakCharacters.length >= 2) {
    // This is basic - just showing potential weak character combinations
    for (let i = 0; i < Math.min(weakCharacters.length - 1, 3); i++) {
      ngrams.push(weakCharacters[i] + weakCharacters[i + 1])
    }
  }

  return {
    characters: weakCharacters,
    ngrams: ngrams.slice(0, 5),
    words: weakWords,
  }
}
